import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBullhorn, FaComments, FaUser, FaTimes, FaBars, FaChevronLeft } from "react-icons/fa";
import api from "../../config/api";
import "./BottomNav.css";

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const navRef = useRef(null);
  const hasLoggedUnreadErrorRef = useRef(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch unread announcements count from API
  const fetchUnreadAnnouncements = async () => {
    try {
      // This endpoint already powers the announcements page and includes unread_count.
      const response = await api.get('/announcements?page=1&per_page=1');
      if (response.data.success) {
        const count = response.data.data?.unread_count || 0;
        setUnreadAnnouncements(count);
        hasLoggedUnreadErrorRef.current = false;
      }
    } catch (error) {
      if (!hasLoggedUnreadErrorRef.current) {
        console.error("Error fetching unread announcements:", error);
        hasLoggedUnreadErrorRef.current = true;
      }

      // Fallback to localStorage if API fails
      const savedCount = localStorage.getItem('announcements_unread_count');
      if (savedCount) {
        setUnreadAnnouncements(parseInt(savedCount, 10));
      } else {
        setUnreadAnnouncements(0);
      }
    }
  };

  // Calculate total unread messages from chat
  const calculateTotalUnreadMessages = () => {
    try {
      const savedConversations = localStorage.getItem("chat_conversations");
      if (savedConversations) {
        const conversations = JSON.parse(savedConversations);
        const totalUnread = conversations.reduce((total, conv) => {
          return total + (conv.unread_count || 0);
        }, 0);
        return totalUnread;
      }
      return 0;
    } catch (error) {
      console.error("Error calculating unread messages:", error);
      return 0;
    }
  };

  // Update both counts
  const updateAllCounts = async () => {
    setIsLoading(true);
    await fetchUnreadAnnouncements();
    const messagesCount = calculateTotalUnreadMessages();
    setTotalUnreadMessages(messagesCount);
    setIsLoading(false);
  };

  // Initial load and setup
  useEffect(() => {
    updateAllCounts();

    // Listen for announcement updates
    const handleAnnouncementUpdate = (event) => {
      if (event.detail) {
        setUnreadAnnouncements(event.detail.unreadCount || 0);
        localStorage.setItem('announcements_unread_count', event.detail.unreadCount || 0);
      }
    };

    // Listen for chat updates
    const handleChatUpdate = () => {
      setTimeout(() => {
        const messagesCount = calculateTotalUnreadMessages();
        setTotalUnreadMessages(messagesCount);
      }, 100);
    };

    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === "chat_conversations" || e.key === null) {
        setTimeout(() => {
          const messagesCount = calculateTotalUnreadMessages();
          setTotalUnreadMessages(messagesCount);
        }, 100);
      }
    };

    // Listen for custom events
    window.addEventListener("announcementsUpdated", handleAnnouncementUpdate);
    window.addEventListener("chatUpdated", handleChatUpdate);
    window.addEventListener("newMessageReceived", handleChatUpdate);
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", updateAllCounts);

    // Set up interval for periodic updates (every 10 seconds)
    const interval = setInterval(updateAllCounts, 10000);

    return () => {
      window.removeEventListener("announcementsUpdated", handleAnnouncementUpdate);
      window.removeEventListener("chatUpdated", handleChatUpdate);
      window.removeEventListener("newMessageReceived", handleChatUpdate);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", updateAllCounts);
      clearInterval(interval);
    };
  }, []);

  // Check for announcement updates when navigating
  useEffect(() => {
    if (location.pathname === "/announcements") {
      // When on announcements page, mark as read and update count
      setTimeout(updateAllCounts, 500);
    } else if (location.pathname === "/messages") {
      // When on messages page, update message count
      setTimeout(() => {
        const messagesCount = calculateTotalUnreadMessages();
        setTotalUnreadMessages(messagesCount);
      }, 500);
    }
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    setIsExpanded(false);

    if (path === "/announcements") {
      // Reset local announcement badge when navigating to announcements
      setTimeout(() => {
        setUnreadAnnouncements(0);
        localStorage.setItem('announcements_unread_count', '0');
        // Dispatch event for announcements component
        window.dispatchEvent(new CustomEvent('announcementsViewed'));
      }, 300);
    }

    if (path === "/messages") {
      // Update message count after navigation
      setTimeout(() => {
        const messagesCount = calculateTotalUnreadMessages();
        setTotalUnreadMessages(messagesCount);
      }, 500);
    }
  };

  const toggleMenu = () => {
    setIsExpanded(!isExpanded);
  };

  const closeMenu = () => {
    setIsExpanded(false);
  };

  // Function to manually update counts (exposed globally)
  useEffect(() => {
    window.updateBottomNavCounts = updateAllCounts;
    return () => {
      delete window.updateBottomNavCounts;
    };
  }, []);

  // Calculate total notifications
  const totalNotifications = unreadAnnouncements + totalUnreadMessages;

  return (
    <>
      {/* Floating Action Button */}
      <div className={`fab ${isExpanded ? 'expanded' : ''}`} onClick={toggleMenu}>
        <div className="fab-icon">
          {isExpanded ? <FaTimes /> : <FaBars />}
        </div>
        {/* Hide notification counter when menu is open */}
        {(!isLoading && totalNotifications > 0 && !isExpanded) && (
          <span className="fab-badge">
            {totalNotifications > 99 ? '99+' : totalNotifications}
          </span>
        )}
      </div>

      {/* Overlay when menu is open */}
      {isExpanded && <div className="fab-overlay" onClick={closeMenu} />}

      {/* Expanded Menu */}
      <div className={`fab-menu ${isExpanded ? 'visible' : ''}`} ref={navRef}>
        {/* Menu Header */}
        <div className="fab-menu-header">
          <h3 className="fab-menu-title">Quick Action</h3>
          <button className="fab-menu-close" onClick={closeMenu}>
            <FaChevronLeft />
          </button>
        </div>

        {/* Menu Items */}
        <div className="fab-menu-content">
          <div
            className={`fab-menu-item ${isActive("/announcements") ? "active" : ""}`}
            onClick={() => handleNavigation("/announcements")}
          >
            <div className="fab-menu-item-content">
              <div className="fab-menu-icon-wrapper">
                <FaBullhorn className="fab-menu-icon" />
                {!isLoading && unreadAnnouncements > 0 && (
                  <span className="menu-badge announcement-badge">
                    {unreadAnnouncements > 99 ? '99+' : unreadAnnouncements}
                  </span>
                )}
              </div>
              <div className="fab-menu-text">
                <span className="fab-menu-label">Announcements</span>
                <span className="fab-menu-subtitle">Latest updates and news</span>
              </div>
            </div>
            {isActive("/announcements") && <div className="active-indicator" />}
          </div>

          <div
            className={`fab-menu-item ${isActive("/messages") ? "active" : ""}`}
            onClick={() => handleNavigation("/messages")}
          >
            <div className="fab-menu-item-content">
              <div className="fab-menu-icon-wrapper">
                <FaComments className="fab-menu-icon" />
                {!isLoading && totalUnreadMessages > 0 && (
                  <span className="menu-badge message-badge">
                    {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                  </span>
                )}
              </div>
              <div className="fab-menu-text">
                <span className="fab-menu-label">Messages</span>
                <span className="fab-menu-subtitle">Chat with members</span>
              </div>
            </div>
            {isActive("/messages") && <div className="active-indicator" />}
          </div>

          <div
            className={`fab-menu-item ${isActive("/profile") ? "active" : ""}`}
            onClick={() => handleNavigation("/profile")}
          >
            <div className="fab-menu-item-content">
              <div className="fab-menu-icon-wrapper">
                <FaUser className="fab-menu-icon" />
              </div>
              <div className="fab-menu-text">
                <span className="fab-menu-label">Profile</span>
                <span className="fab-menu-subtitle">Your account settings</span>
              </div>
            </div>
            {isActive("/profile") && <div className="active-indicator" />}
          </div>
        </div>

        {/* Menu Footer */}
        <div className="fab-menu-footer">
          <p className="fab-menu-footer-text">Tap outside or use back button to close</p>
        </div>
      </div>
    </>
  );
};

export default BottomNav;